// ============================================================================
// Tests Service
// ============================================================================
// Description: Service to manage test execution through backend API
// Features: Test file execution, output capture, secure whitelisted access
// Usage: Execute backend, integration, and E2E tests with terminal output
// ============================================================================

import axios from 'axios';

export interface TestFile {
  id: string;
  name: string;
  path: string;
  description: string;
  category: string;
  type: 'python' | 'typescript' | 'integration' | 'e2e';
}

export interface TestResult {
  success: boolean;
  output: string;
  duration: number;
  error?: string;
}

export interface TestStatistics {
  total_files: number;
  total_tests: number;
  passed: number;
  failed: number;
  errors: number;
  skipped: number;
  success_rate: number;
  total_duration: number;
  categories: Record<string, {
    files: number;
    passed: number;
    failed: number;
    errors: number;
    duration: number;
  }>;
}

export interface AllTestsResult {
  success: boolean;
  statistics: TestStatistics;
  results: (TestResult & { file_info: TestFile })[];
  summary: string;
  error?: string;
}

class TestsService {
  private baseURL = 'http://localhost:8090'; // Simple external test runner

  // Execute a test file via external simple test runner
  async runTest(testPath: string, testType: 'python' | 'typescript' | 'integration' | 'e2e'): Promise<TestResult> {
    try {
      console.log('TestsService: Running test via external server:', testPath);
      console.log('TestsService: Making request to:', `${this.baseURL}/run-test`);
      
      const startTime = Date.now();
      
      // Use POST request to external simple test runner
      const response = await axios.post(`${this.baseURL}/run-test`, {
        testPath,
        testType
      }, {
        timeout: 350000, // 6 minute timeout for tests
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const duration = Date.now() - startTime;

      if (response.data && response.data.success !== undefined) {
        return {
          success: response.data.success,
          output: response.data.output || 'No output available',
          duration: response.data.duration || duration,
          error: response.data.error
        };
      } else {
        throw new Error('Invalid response format from external test runner');
      }
      
    } catch (error: any) {
      console.error('TestsService: Error running test via external runner:', error);
      
      let errorMessage = 'Failed to run test via external runner';
      
      if (error.response) {
        // External server responded with error status
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 404) {
          errorMessage = 'External test runner not found. Please start: python3 ~/simple_test_runner.py';
        } else if (status === 500) {
          errorMessage = data?.error || 'External test runner internal error';
        } else {
          errorMessage = data?.error || `External test runner error (${status})`;
        }
      } else if (error.request) {
        // Request made but no response - likely external server not running
        errorMessage = 'Cannot reach external test runner. Please start: python3 ~/simple_test_runner.py';
      } else {
        // Something else happened
        errorMessage = error.message || 'Unknown error occurred';
      }

      return {
        success: false,
        output: '',
        duration: 0,
        error: errorMessage
      };
    }
  }

  // Execute all tests and get comprehensive statistics
  async runAllTests(): Promise<AllTestsResult> {
    try {
      console.log('📊 Starting comprehensive test suite...');

      const response = await axios.post(`${this.baseURL}/run-test`, {
        runAll: true
      }, {
        timeout: 600000, // 10 minute timeout for running all tests
        headers: {
          'Content-Type': 'application/json',
        }
      });

      console.log('✅ All tests execution completed');
      return response.data;

    } catch (error) {
      console.error('❌ Error running all tests:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNREFUSED') {
          throw new Error('Test runner server is not accessible. Please ensure the external test runner is running on localhost:8090.');
        } else if (error.response?.status === 404) {
          throw new Error('Run all tests endpoint not found. Please check the test runner configuration.');
        } else if (error.response?.data?.error) {
          throw new Error(`Test execution failed: ${error.response.data.error}`);
        } else {
          throw new Error(`Test execution failed: ${error.message}`);
        }
      }
      
      throw error;
    }
  }

  // Get the health status of the tests API
  async getHealthStatus(): Promise<boolean> {
    try {
      const response = await axios.get(`${this.baseURL}/health`, {
        timeout: 5000,
        withCredentials: false
      });
      return response.data?.status === 'ok';
    } catch (error) {
      console.error('TestsService: Health check failed:', error);
      return false;
    }
  }

  // Restart the external test runner
  async restartTestRunner(): Promise<{success: boolean; message: string}> {
    try {
      console.log('🔄 Attempting to restart test runner...');

      // First, try to check if server is running
      const isCurrentlyRunning = await this.getHealthStatus();
      
      if (isCurrentlyRunning) {
        // Server is running, use the restart endpoint
        console.log('📡 Server is running, using restart endpoint...');
        
        const response = await axios.get(`${this.baseURL}/restart-runner`, {
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json',
          }
        });

        console.log('✅ Restart request sent successfully');
      } else {
        // Server is down, use startup service to start it
        console.log('🚀 Server is down, using startup service...');
        
        try {
          const startupResponse = await axios.get('http://localhost:8091/start-test-runner', {
            timeout: 15000,
          });
          
          if (startupResponse.data.success) {
            console.log('✅ Startup service successfully started test runner');
          } else {
            console.warn('⚠️ Startup service failed:', startupResponse.data.error);
          }
        } catch (startupError) {
          console.warn('⚠️ Could not reach startup service:', startupError);
          // Continue anyway, maybe the runner will start via other means
        }
      }
      
      // Wait a few seconds for restart/start to complete
      console.log('⏳ Waiting for server to start...');
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      // Check if new instance is healthy (try multiple times)
      let isHealthy = false;
      for (let i = 0; i < 3; i++) {
        console.log(`🔍 Health check attempt ${i + 1}/3...`);
        isHealthy = await this.getHealthStatus();
        if (isHealthy) break;
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      return {
        success: isHealthy,
        message: isHealthy 
          ? 'Test runner started successfully! 🎉' 
          : 'Could not start test runner automatically. Please run manually: python3 ~/simple_test_runner.py'
      };

    } catch (error) {
      console.error('❌ Error restarting test runner:', error);
      
      return {
        success: false,
        message: 'Could not restart automatically. Please run manually: python3 ~/simple_test_runner.py'
      };
    }
  }
}

// Export singleton instance
const testsService = new TestsService();
export default testsService;